import React from "react";
import { Navigate, Route, useLocation } from "react-router";
import Cookie from "js-cookie";
import Dashboard from "@/Pages/Dashboard";
import useRebelContext from "@/hooks/useRebelContext";
import useSetInviteToken from "@/hooks/useSetInviteToken";
import SignInContainer from "@/Pages/Auth/SignIn/SignInContainer";
import LoadingComponent from "@/components/Loading/loadingComponent";
import ScrollToTop from "@/components/AppLayout/MainLayout/components/ScrollToTop";
import Toast from "@/components/common/Toast";
import DoRedirect from "@/components/DoRedirect";
import { invitationTypes } from "@/store/invitations/invitationActions";
import * as Lazy from "./lazyRoutes";
import {
  EmptyLayoutOutlet,
  MainLayoutOutlet,
  ProtectedOutlet,
  SuspenseOutlet,
} from "./components/RouteWrapper";
import { withParams } from "./components/withParams";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import { eventRoutes, hubRoutes, projectRoutes } from "./components/RebelRouter";

const CONTACT_US_URL = {
  ENG: "https://rebelbase.co/contact-us",
  ESP: "https://rebelbase.co/es/contact-us/",
};

/**
 * `/settings` is kept only as a redirect to the canonical `/me/settings`, for
 * bookmarks and any link not yet updated. The search string and hash have to
 * survive the hop: later tasks add `?tab=` deep-linking on this page, and a
 * bookmark to `/settings?tab=notifications` silently landing on the default
 * tab would be a regression, not a redirect.
 */
const SettingsRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/me/settings${search}${hash}`} replace />;
};

/**
 * Root layout. Owns the app-wide chrome plus the entry redirects that used to
 * run at the top of the old <Switch>.
 */
const RootLayout = () => {
  const { userId, lang } = useRebelContext();
  const { pathname, search } = useLocation();
  const isLoggedIn = !!Cookie.get("token");

  // Side effect only: captures an invitation token off the sign-up query string.
  useSetInviteToken(pathname, search);

  if (pathname === "/contact-us") {
    window.location.replace(CONTACT_US_URL[lang] ?? CONTACT_US_URL.ENG);
    return <LoadingComponent />;
  }

  const atEntryPoint = pathname === "/" || pathname.includes("/auth");

  if (isLoggedIn && atEntryPoint) {
    // Signed in and sitting on a pre-auth page — send them to the app.
    // Before the profile resolves there is nowhere to send them yet, so wait.
    if (userId) return <Navigate to="/dashboard" replace />;
    return <LoadingComponent />;
  }

  if (!isLoggedIn && pathname === "/") {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <>
      <ScrollToTop />
      {/* The layout groups below carry their own boundary. This one covers the
          routes that hang directly off this layout — invitation acceptance,
          email verification, research-data — which are lazy now too. */}
      <SuspenseOutlet />
      <Toast />
      <DoRedirect />
    </>
  );
};

export const routes = (
  <Route element={<RootLayout />} errorElement={<RouteErrorBoundary />}>
    {/* Invitation acceptance — reachable while signed out. */}
    <Route
      path="/accept/create-hub-invitation"
      element={<Lazy.AcceptInvitation invitationType={invitationTypes.createHub} />}
    />
    <Route
      path="/accept/hub-event-invitation"
      element={<Lazy.AcceptInvitation invitationType={invitationTypes.hubEventInvitation} />}
    />
    <Route
      path="/accept/hub-invitation"
      element={<Lazy.AcceptInvitation invitationType={invitationTypes.hubInvitation} />}
    />
    <Route
      path="/accept/project-invitation"
      element={<Lazy.AcceptInvitation invitationType={invitationTypes.projectInvitation} />}
    />
    <Route path="/email-verification" element={<Lazy.EmailVerification />} />
    <Route path="/research-data-2020" element={<Lazy.ResearchData />} />

    {/* Pre-auth pages, on the bare layout. */}
    <Route element={<EmptyLayoutOutlet />}>
      <Route path="/forgot-password" element={<Lazy.ForgotPassword />} />
      <Route path="/accept/reset-password" element={<Lazy.ResetPassword />} />
      <Route path="/auth/login" element={<SignInContainer />} />
      <Route path="/auth/sign-up" element={withParams(Lazy.SignUp)} />
    </Route>

    {/* Everything else runs inside the app chrome. */}
    <Route element={<MainLayoutOutlet />}>
      {eventRoutes}
      {projectRoutes}
      {hubRoutes}

      {/* Profile reads :u_id with useParams, so it needs no withParams shim. */}
      <Route path="/profile/:u_id" element={<Lazy.Profile />} />
      <Route path="/browse/*" element={withParams(Lazy.Browse)} />

      <Route element={<ProtectedOutlet />}>
        <Route path="/dashboard" element={withParams(Dashboard)} />
        <Route path="/notifications" element={withParams(Lazy.Notifications)} />
        <Route path="/invitations" element={withParams(Lazy.Invitations)} />
        <Route path="/me/settings" element={withParams(Lazy.Settings)} />
        <Route path="/settings" element={<SettingsRedirect />} />
        <Route path="/create-hub/:h_id" element={withParams(Lazy.CreateHub)} />
        <Route path="/create-hub/:h_id/:step" element={withParams(Lazy.CreateHub)} />
      </Route>

      <Route path="*" element={<Lazy.NoMatch />} />
    </Route>
  </Route>
);
